import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import { EditableText as T } from "@/components/live/EditableText";
import { EditableImage } from "@/components/live/EditableImage";
import { useContent, resolveUrl } from "@/lib/content";
import { Section } from "@/components/live/Section";

// Curated vertical images for home page (J Alex pool photos)
const curatedVerticalImages = [
  { id: "curated-1", url: "https://customer-assets.emergentagent.com/job_7df16810-7def-4b95-966c-e0aa10481ae2/artifacts/i98dvvh5_image10860.JPG", alt: "Photo by J Alex" },
  { id: "curated-2", url: "https://customer-assets.emergentagent.com/job_7df16810-7def-4b95-966c-e0aa10481ae2/artifacts/oep4uion_image10904.JPG", alt: "Photo by J Alex" },
];

const DEFAULT_HERO = "https://customer-assets.emergentagent.com/job_kalani-luxe/artifacts/4e805lsr_unnamed.jpg";
export const HOME_FEATURE_KEYS = [1, 2, 3, 4].map((i) => `home.feature.${i}`);

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [fallbackImages, setFallbackImages] = useState(curatedVerticalImages);
  const { get } = useContent();

  const fetchGalleryImages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/gallery`);
      const portraits = (response.data || []).filter((img) => img.category === "portraits");
      setFallbackImages([...portraits.slice(0, 2), ...curatedVerticalImages]);
    } catch (error) {
      logError("Failed to fetch gallery images:", error);
    }
  }, []);

  useEffect(() => {
    setIsVisible(true);
    fetchGalleryImages();
  }, [fetchGalleryImages]);

  const hero = get("hero_image");
  const heroPosition = hero?.metadata?.position || "top";
  const featureSlots = HOME_FEATURE_KEYS.map((k, i) => ({ k, fallback: fallbackImages[i] }));

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <EditableImage
            k="hero_image"
            data-testid="hero-image"
            src={DEFAULT_HERO}
            alt="Kalani Luana"
            className="w-full h-full object-cover"
            style={{ objectPosition: heroPosition }}
          />
          <div className="hero-overlay" />
        </div>

        <div className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <T k="home.hero.eyebrow" as="p" className="font-body text-sm tracking-widest uppercase text-stone-grey mb-4 stagger-1 animate-fade-in">Welcome to My World</T>
          <T k="home.hero.title" as="h1" className="font-heading text-5xl md:text-7xl text-charcoal mb-6 stagger-2 animate-fade-in">Kalani Luana</T>
          <T k="home.hero.subtitle" as="p" className="font-body text-xl md:text-2xl font-medium text-charcoal mb-12 stagger-3 animate-fade-in tracking-wide">XXX Film Starlet & Elite Companion</T>

          <div className="flex flex-col sm:flex-row gap-4 justify-center stagger-5 animate-fade-in">
            <Link to="/about">
              <Button className="btn-primary" data-testid="learn-more-btn">
                <T k="home.hero.cta_primary">Discover More</T>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/booking">
              <Button className="btn-secondary" data-testid="booking-btn">
                <T k="home.hero.cta_secondary">Request Introduction</T>
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-jade-deep/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-jade-deep/50 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <Section k="home.section.intro-section" label="intro" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="intro-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <div className="image-frame aspect-[3/4]">
                <EditableImage k="home.intro.image" src="https://customer-assets.emergentagent.com/job_kalani-luxe/artifacts/jfbl5mbo_picnic.jpg" alt="Kalani portrait" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="md:col-span-7 md:pl-12">
              <T k="home.intro.title" as="h2" className="font-heading text-4xl md:text-5xl text-charcoal mb-6">The Art of Connection</T>
              <T k="home.intro.p1" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-6">I'm Kalani — a passionate soul who believes that the finest moments in life are those shared in the company of someone who truly understands the value of presence, conversation, and genuine connection.</T>
              <T k="home.intro.p2" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-8">Whether accompanying you to an upscale dinner, a cultural event, or simply enjoying an intimate evening of meaningful conversation, I bring a unique combination of sophistication, playfulness, and authenticity to every encounter.</T>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-jade-text">
                  <MapPin className="w-4 h-4" />
                  <T k="home.intro.loc1" className="font-body text-sm">NYC / NJ</T>
                </div>
                <div className="flex items-center gap-2 text-jade-text">
                  <MapPin className="w-4 h-4" />
                  <T k="home.intro.loc2" className="font-body text-sm">California</T>
                </div>
                <div className="flex items-center gap-2 text-jade-text">
                  <Sparkles className="w-4 h-4" />
                  <T k="home.intro.loc3" className="font-body text-sm">Available for Travel</T>
                </div>
              </div>

              <Link to="/about">
                <Button className="btn-secondary" data-testid="read-more-btn">
                  <T k="home.intro.cta">Read My Full Story</T>
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* Featured Image Section */}
      <Section k="home.section.featured-section" label="featured" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="featured-section">
        <div className="max-w-7xl mx-auto text-center">
          <T k="home.featured.eyebrow" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Featured</T>
          <T k="home.featured.title" as="h2" className="font-heading text-4xl md:text-5xl text-charcoal mb-12">A Moment Captured</T>

          <div className="image-frame aspect-[21/9] max-w-5xl mx-auto rounded-2xl overflow-hidden">
            <EditableImage k="home.featured.image" src="https://customer-assets.emergentagent.com/job_7df16810-7def-4b95-966c-e0aa10481ae2/artifacts/30zkppq3_image10868.JPG" alt="Kalani Luana - A Moment Captured" className="w-full h-full object-cover object-top" />
          </div>

          <div className="mt-12">
            <Link to="/booking-etiquette">
              <Button className="btn-primary" data-testid="view-etiquette-btn">
                <T k="home.featured.cta">View Booking Etiquette</T>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Gallery Preview */}
      <Section k="home.section.gallery-preview" label="gallery preview" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <T k="home.gallery.eyebrow" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Portfolio</T>
            <T k="home.gallery.title" as="h2" className="font-heading text-4xl md:text-5xl text-charcoal mb-6">Photos Worth a Thousand Words</T>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featureSlots.map(({ k, fallback }, index) => (
              <div key={k} className={`gallery-item aspect-[3/4] ${index % 2 === 1 ? 'md:mt-8' : ''}`} data-testid={`home-feature-slot-${index + 1}`}>
                {(fallback || get(k)?.content) ? (
                  <EditableImage k={k} src={resolveUrl(fallback?.url)} alt={fallback?.alt || `Gallery ${index + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-sand/30" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button className="btn-secondary" data-testid="view-gallery-btn">
                <T k="home.gallery.cta">View Full Gallery</T>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Quote Section */}
      <Section k="home.section.quote-section" label="quote" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-jade-deep" data-testid="quote-section">
        <div className="max-w-4xl mx-auto text-center">
          <T k="home.quote.text" as="blockquote" multiline className="font-heading text-2xl md:text-4xl text-ivory leading-relaxed mb-8 italic">"Imperfection is beauty, madness is genius and it's better to be absolutely ridiculous than absolutely boring."</T>
          <T k="home.quote.author" as="p" className="font-accent text-3xl text-champagne-gold">— Marilyn Monroe</T>
        </div>
      </Section>

      {/* Newsletter Section */}
      <Section k="home.section.newsletter-section" label="newsletter" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto text-center">
          <T k="home.newsletter.eyebrow" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Stay Connected</T>
          <T k="home.newsletter.title" as="h2" className="font-heading text-4xl md:text-5xl text-charcoal mb-6">Join My Inner Circle</T>
          <T k="home.newsletter.text" as="p" multiline className="font-body text-stone-grey mb-12 leading-relaxed">Be the first to know about my travel schedule, exclusive updates, and special announcements. Your privacy is always respected.</T>
          <NewsletterSignup />
        </div>
      </Section>

      {/* CTA Section */}
      <Section k="home.section.cta-section" label="cta" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-blush-pink/30" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <T k="home.cta.title" as="h2" className="font-heading text-4xl md:text-5xl text-charcoal mb-6">Ready to Connect?</T>
          <T k="home.cta.text" as="p" multiline className="font-body text-stone-grey mb-12 leading-relaxed max-w-2xl mx-auto">I look forward to learning about you and exploring the possibility of creating something memorable together. Begin by reviewing my booking etiquette, then submit an inquiry.</T>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking-etiquette">
              <Button className="btn-secondary" data-testid="etiquette-cta-btn">
                <T k="home.cta.secondary">Booking Etiquette</T>
              </Button>
            </Link>
            <Link to="/booking">
              <Button className="btn-primary" data-testid="inquiry-cta-btn">
                <T k="home.cta.primary">Submit Inquiry</T>
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
